import React, { useEffect, useState } from "react";
import { getDefaultSettingCookie, setDefaultSettingCookie } from "utils/cookies/cookies";
import useNoteMateStore from "store/useNoteMateStore";
import { useConfirmPrivilege } from "utils/customHook/useConfirmPrivilege";
import { SlidFeatures } from "utils/privilegeManager";
import { eventTypes } from "types/eventTracking";
import { trackEvent } from "utils/eventTracking";
import { setIsListOpen } from "redux/actions/vdocsActions";
import { useAppDispatch } from "hooks";
import { getApplicationType, getEventLocationMode } from "utils/utils";
import { MainButton } from "./MainButton";
import styled from "styled-components";
import { NewFeatureBanner } from "./NewFeatureBanner";
import { GuideTooltip } from "./GuideTooltip";

export const NoteMateButton = ({ location }: { location: "plainNote" | "videoNote" }) => {
  const [showPlainNoteBanner, setShowPlainNoteBanner] = useState(false);
  const [showDesktopVideoNoteBanner, setShowDesktopVideoNoteBanner] = useState(false);
  const [showGuideTooltip, setShowGuideTooltip] = useState(false);

  const { openNoteMate, isNoteMateOpen } = useNoteMateStore();
  const { confirmPrivilege, showInsufficientPrivilegeModal } = useConfirmPrivilege();
  const dispatch = useAppDispatch();
  const applicationType = getApplicationType();

  useEffect(() => {
    const isPlainNoteBannerVisible = getDefaultSettingCookie({ property: "isPlainNoteBannerVisible" });
    const isDesktopVideoNoteBannerVisible = getDefaultSettingCookie({ property: "isDesktopVideoNoteBannerVisible" });

    //show new feature tooltip for plain note
    if (location === "plainNote") {
      if (isPlainNoteBannerVisible === undefined) {
        setShowPlainNoteBanner(true);
        setDefaultSettingCookie({ property: "isPlainNoteBannerVisible", value: true });
      }
      if (isPlainNoteBannerVisible === true) {
        setShowPlainNoteBanner(true);
      }
    }

    //show new feature tooltip for desktop video note
    if (location === "videoNote" && applicationType === "desktop") {
      if (isDesktopVideoNoteBannerVisible === undefined) {
        setDefaultSettingCookie({ property: "isDesktopVideoNoteBannerVisible", value: true });
        setShowDesktopVideoNoteBanner(true);
      }
      if (isDesktopVideoNoteBannerVisible === true) {
        setShowDesktopVideoNoteBanner(true);
      }
    }

    // hide banner if the location is changed
    if (location !== "plainNote") {
      setShowPlainNoteBanner(false);
    }
    if (location !== "videoNote" || applicationType !== "desktop") {
      setShowDesktopVideoNoteBanner(false);
    }
  }, [location, applicationType]);

  const hideBanner = () => {
    if (location === "plainNote") {
      setDefaultSettingCookie({ property: "isPlainNoteBannerVisible", value: false });
      setShowPlainNoteBanner(false);
    } else if (location === "videoNote" && applicationType === "desktop") {
      setDefaultSettingCookie({ property: "isDesktopVideoNoteBannerVisible", value: false });
      setShowDesktopVideoNoteBanner(false);
    }
  };

  const handleNoteMateButtonClick = () => {
    if (isNoteMateOpen) return;

    hideBanner();

    trackEvent({
      eventType: eventTypes.click.NOTE_MATE,
      eventProperties: {
        from: getEventLocationMode(),
      },
    });

    if (!confirmPrivilege(SlidFeatures.noteMate)) return showInsufficientPrivilegeModal();

    openNoteMate();
    if (location === "plainNote") dispatch(setIsListOpen(false));
  };

  const handleNoteMateButtonHover = (mouseEnter: boolean) => {
    if (showPlainNoteBanner || showDesktopVideoNoteBanner) return;

    setShowGuideTooltip(mouseEnter);
  };

  return (
    <NoteButtonWrapper location={location}>
      <MainButton
        location={location}
        handleNoteMateButtonClick={handleNoteMateButtonClick}
        handleNoteMateButtonHover={handleNoteMateButtonHover}
        showBanner={showPlainNoteBanner || showDesktopVideoNoteBanner}
      />
      {(showPlainNoteBanner || showDesktopVideoNoteBanner) && (
        <TooltipContainer location={location}>
          <NewFeatureBanner hideBanner={hideBanner} location={location} />
        </TooltipContainer>
      )}
      {showGuideTooltip && !isNoteMateOpen && (
        <TooltipContainer location={location}>
          <GuideTooltip />
        </TooltipContainer>
      )}
    </NoteButtonWrapper>
  );
};

const NoteButtonWrapper = styled.div<{ location: "plainNote" | "videoNote" }>`
  background-color: var(--white);
  border-radius: ${({ location }) => (location === "plainNote" ? "15px" : "12px")};

  ${({ location }) => location === "plainNote" && `position: absolute; bottom: 20px; left: 28px; z-index: 100;`}
`;

const TooltipContainer = styled.div<{ location: "plainNote" | "videoNote" }>`
  position: absolute;
  left: 0px;
  ${({ location }) => location === "plainNote" && `bottom: 56px;`}
  ${({ location }) => location === "videoNote" && `bottom: 70px;`}
`;
